import React, { useRef, useState, useEffect } from 'react';
import { AppBar, Container, Button, Box, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, Paper, TextField, Menu, MenuItem, 
  Select, InputLabel, FormControl, Checkbox, ListItemText ,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Toolbar 
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import CloseIcon from '@mui/icons-material/Close';

import BuxferTransactionSmartSearch from '../components/BuxferTransactionSmartSearch'
import Sidebar from '../components/Sidebar';
import buxferTransactions from '../services/buxfer.service';
import DraggableTable from '../components/DraggableTable';

export default function BuxferManagerPage() {
  const initialParamsApplied = useRef(false);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState('');
  const [selectedBankFilter, setSelectedBankFilter] = useState('');
  const [distinctCustomers, setDistinctCustomers] = useState([]);
  const [distinctBanks, setDistinctBanks] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [dateFilter, setDateFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amountFilter, setAmountFilter] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [orderBy, setOrderBy] = useState('date');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [dateFilterOptions] = useState([
    //"THIS_WEEK",
    //"THIS_MONTH",
    //"THIS_YEAR",
    //"LAST_WEEK",
    "LAST_MONTH",
    "LAST_YEAR",
    // "LAST_7_DAYS",
    // "LAST_30_DAYS",
    // "LAST_60_DAYS",
    // "LAST_90_DAYS",
    // "LAST_180_DAYS",
    // "LAST_365_DAYS",
    // "LAST_3_MONTHS",
    // "LAST_6_MONTHS",
    // "LAST_12_MONTHS",
    // "NEXT_WEEK",
    // "NEXT_MONTH",
    // "NEXT_YEAR",
    // "NEXT_3_MONTHS",
    // "NEXT_6_MONTHS",
    // "NEXT_12_MONTHS"
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const buttonRef = useRef();
  const [selectedCard, setSelectedCard] = useState(null);
  const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
  const [accountNameHeader, setAccountNameHeader] = useState('');
  const [income, setIncome] = useState(0);
  const [expense, setExpense] = useState(0);
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const transactionColumns = [
    { id: 'date', label: 'Date', sortable: true, sortKey: 'date' },
    { 
      id: 'amount', 
      label: 'Amount', 
      sortable: true, 
      sortKey: 'amount',
      // Custom render function
      render: (row) => (
        <span style={{ color: row.amount < 0 ? 'red' : 'green', fontWeight: 500 }}>
          {row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    { id: 'description', label: 'Description', sortable: true, sortKey: 'description' },
    { id: 'tags', label: 'Tags', sortable: true, sortKey: 'tags' },
    { id: 'customer_name', label: 'Customer Name', sortable: false, sortKey: null },
    { 
      id: 'account_name', 
      label: 'Account Name', 
      sortable: true, 
      sortKey: 'accountName',
      render: (row) => (
        <span 
          style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
          onClick={() => onBankSelect(row.account_name, '', true)}
        >
          {row.account_name}
        </span>
      )
    },
  ];

  // 👈 Helper: Get columns with saved order applied
  const getDisplayedColumns = () => {
    const columns = transactionColumns;
    try {
      const savedOrder = localStorage.getItem('buxfer_manager_column_order');
      if (savedOrder) {
        const orderArray = JSON.parse(savedOrder);
        // Sort columns by saved order
        return columns.sort((a, b) => {
          const aIndex = orderArray.indexOf(a.id);
          const bIndex = orderArray.indexOf(b.id);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }
    } catch (e) {
      console.error('Failed to apply saved column order', e);
    }
    return columns;
  };

  const handleOrderByChange = (field) => {
    if (orderBy === field) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrderDirection('asc');
    }
  };

  const handleColumnsReorder = (reorderedColumns) => {
    // Save the new column order to page-specific localStorage
    try {
      const columnOrder = reorderedColumns.map(col => col.id);
      localStorage.setItem('buxfer_manager_column_order', JSON.stringify(columnOrder));
    } catch (e) {
      console.error('Failed to save column order', e);
    }
  };

  const updateQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', page + 1);
    params.set('rowsPerPage', rowsPerPage);
    if (searchKeyword) params.set('search', searchKeyword);
    if (selectedAccount) params.set('email', selectedAccount);
    if (accountNameHeader) params.set('accountName', accountNameHeader);
    if (selectedStatuses.length) params.set('status', selectedStatuses.join(','));
    if (selectedTypes.length) params.set('types', selectedTypes.join(','));
    if (selectedTags.length) params.set('tags', selectedTags.join(','));
    if (dateFilter && !startDate && !endDate) params.set('dateFilter', dateFilter); // Only set if no custom dates
    if (startDate) params.set('startDate', startDate); // 👇 Add this
    if (endDate) params.set('endDate', endDate);       // 👇 Add this
    if (amountFilter) params.set('amount', amountFilter);  // Add amount filter to params
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const parseQueryArray = (value) => {
    if (!value) return [];
    if (value.includes(',')) return value.split(',').map(v => v.trim());
    return [value];
  };

  const handleAccountClick = async (cardIdentifier) => {
    try {
      const res = await buxferTransactions.getCardByIdentifier(cardIdentifier);
      if (res.success && res.result?.card) {
        setSelectedCard(res.result.card);
        setIsCardDialogOpen(true);
      } else {
        setSelectedCard(null);
        alert('Card not found');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to fetch card details');
    }
  };

  const handleCloseDialog = () => {
    setIsCardDialogOpen(false);
    setSelectedCard(null);
  };

  useEffect(() => {
    const buildParamsArray = arr => (arr.length ? arr.join(',') : '');
    const params = {
      keyword:    searchKeyword,
      accountId:  selectedAccount,
      status:     buildParamsArray(selectedStatuses),
      types:      buildParamsArray(selectedTypes),
      tags:       buildParamsArray(selectedTags),
      dateFilter: dateFilter,
      startDate:   startDate,
      endDate:     endDate,
      amount:     amountFilter
    };
    Promise.all([
      buxferTransactions.getUniqueUsers(
        params.keyword,
        params.accountId,
        selectedStatuses,
        selectedTypes,
        selectedTags,
        params.dateFilter,
        params.startDate,
        params.endDate,
        params.amount
      ),
      buxferTransactions.getUniqueTypes(
        params.keyword,
        params.accountId,
        selectedStatuses,
        selectedTypes,
        selectedTags,
        params.dateFilter,
        params.amount
      ),
      buxferTransactions.getUniqueTags(
        params.keyword,
        params.accountId,
        selectedStatuses,
        selectedTypes,
        selectedTags,
        params.dateFilter,
        params.amount
      ),
      buxferTransactions.getUniqueStatuses(
        params.keyword,
        params.accountId,
        selectedStatuses,
        selectedTypes,
        selectedTags,
        params.dateFilter,
        params.amount
      )
    ])
      .then(([uResp, tResp, gResp, sResp]) => {
        if (uResp.success) setAccounts(uResp.result);
        if (tResp.success) setTypes(tResp.result);
        if (gResp.success) setTags(gResp.result);
        if (sResp.success) setStatuses(sResp.result);
      })
      .catch(err => {
        console.error('Error updating dropdowns', err);
      });
    // fetch distinct customers and banks for filters
    (async () => {
      try {
        const cResp = await buxferTransactions.getDistinctCustomers();
        if (cResp.success) setDistinctCustomers(cResp.result || []);
        const bResp = await buxferTransactions.getDistinctBanks();
        if (bResp.success) setDistinctBanks(bResp.result || []);
      } catch (err) {
        console.error('Error fetching distinct filters', err);
      }
    })();
  }, [
    searchKeyword,
    selectedAccount,
    selectedStatuses.join(','),
    selectedTypes.join(','),
    selectedTags.join(','),
    dateFilter,
    amountFilter
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = parseInt(params.get('page'), 10);
    const rowsPerPageParam = parseInt(params.get('rowsPerPage'), 10);
    const searchParam = params.get('search') || '';
  const accountNameParam = (params.get('accountName') || '').trim();
    const accountIdParam = params.get('accountId') || '';
    const statusParam = parseQueryArray(params.get('status'));
    const typesParam = parseQueryArray(params.get('types'));
    const tagsParam = parseQueryArray(params.get('tags'));
    const dateFilterParam = params.get('dateFilter') || '';
    const startDateParam = params.get('startDate') || ''; // 👇 Get param
    const endDateParam = params.get('endDate') || '';     // 👇 Get param
    const amountParam = params.get('amount') || '';
    if (!isNaN(pageParam)) setPage(pageParam - 1);
    if (!isNaN(rowsPerPageParam)) setRowsPerPage(rowsPerPageParam);
    setSearchKeyword(searchParam);
    // If accountName is present in the URL (e.g. opened in a new tab), apply it to header
    if (accountNameParam) {
      setAccountNameHeader(accountNameParam);
    }
    setSelectedAccount(accountIdParam);
    setSelectedStatuses(statusParam);
    setSelectedTypes(typesParam);
    setSelectedTags(tagsParam);
    setDateFilter(dateFilterParam);
    setStartDate(startDateParam); // 👇 Set state
    setEndDate(endDateParam);     // 👇 Set state
    setAmountFilter(amountParam); 
    // Perform a single initial fetch with the parsed params to avoid an initial empty fetch
    const initialPage = !isNaN(pageParam) ? pageParam : (page + 1);
    const initialRows = !isNaN(rowsPerPageParam) ? rowsPerPageParam : rowsPerPage;
    // Mark that we've applied initial params so subsequent updates can write back
    initialParamsApplied.current = true;
    fetchTransactions(
      initialPage,
      initialRows,
      searchParam,
      accountIdParam,
      accountNameParam,
      statusParam,
      typesParam,
      tagsParam,
      dateFilterParam,
      amountParam,
      null, // selectedCustomerFilter
      null, // selectedBankFilter
      orderBy,
      orderDirection,
      startDateParam,
      endDateParam
    );
  }, []);

  useEffect(() => {
    // Skip the automatic fetch until initial URL params (if any) are applied. This avoids
    // an initial fetch with default/empty filters followed by another fetch with parsed params.
    if (!initialParamsApplied.current) return;

    fetchTransactions(
      page + 1,
      rowsPerPage,
      searchKeyword,
      selectedAccount,
      accountNameHeader,
      selectedStatuses,
      selectedTypes,
      selectedTags,
      dateFilter,
      amountFilter,
      selectedCustomerFilter,
      selectedBankFilter,
      orderBy,
      orderDirection, // 👈 Lagyan ng comma dito
      startDate,      // 👈 IDAGDAG ITO (Arg 15)
      endDate         // 👈 IDAGDAG ITO (Arg 16)
    );
    // Only update the browser URL after we have applied any initial params from the URL
    if (initialParamsApplied.current) updateQueryParams();
  }, [page, rowsPerPage, searchKeyword, selectedAccount, accountNameHeader, selectedStatuses, selectedTypes, selectedTags, dateFilter, startDate, endDate, amountFilter, selectedCustomerFilter, selectedBankFilter, orderBy, orderDirection]);

  const fetchTransactions = async (
      page, 
      pageSize, 
      keyword, 
      accountId, 
      accountName, 
      status, 
      types, 
      tags, 
      dateFilter, 
      amount, 
      customerName, 
      bankName, 
      orderBy, 
      orderDirection,
      startDate,
      endDate
    ) => {
      try {
        console.log('[fetchTransactions] params ->', {
          page, pageSize, keyword, accountId, accountName, status, types, tags, dateFilter, amount, 
          customerName, bankName, orderBy, orderDirection
        });

const effectiveDateFilter = (startDate || endDate) ? '' : dateFilter;

        const response = await buxferTransactions.getTransactions(
            page, pageSize, keyword, accountId, accountName, status, types, tags, 
            effectiveDateFilter, // 👈 Use the calculated filter (magiging empty kung may custom dates)
            amount, customerName, bankName, orderBy, orderDirection,
            startDate, // 👈 Ipasa mo bilang bagong argument (Arg 15)
            endDate    // 👈 Ipasa mo bilang bagong argument (Arg 16)
        );

        if (response.success) {
          setTransactions(response.result.transactions);
          setTotalCount(response.result.totalCount);
          setIncome(response.result.incomeSum || 0);
          setExpense(response.result.expenseSum || 0);
        }
      } catch (error) {
          console.error(error);
      }
    };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAccountChange = (email) => {
    setSelectedAccount(email);
    // Reset pagination when changing the selected account so results start from page 1
    setPage(0);
  };

  const handleStatusChange = (event) => {
    setSelectedStatuses(event.target.value);
  };

  const handleTypesChange = (event) => {
    setSelectedTypes(event.target.value);
  };

  const handleTagsChange = (event) => {
    setSelectedTags(event.target.value);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  const handleAmountChange = (event) => {
    setAmountFilter(event.target.value);
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleExportFormat = async (format) => {
    handleCloseMenu();
    try {
      const blob = await buxferTransactions.exportTransactions(
        searchKeyword,
        selectedAccount,
        selectedStatuses,
        selectedTypes,
        selectedTags,
        dateFilter,
        amountFilter,
        orderBy,
        orderDirection,
        format // 👈 this is the export format: 'csv', 'excel', etc.
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const onBankSelect = async (name, _searchKeyword = '', newWindow = false) => {
    // Preserve original behavior when invoked in the same tab
    console.log(name,_searchKeyword,newWindow)
    if (newWindow) {
      const params = new URLSearchParams();
      if (_searchKeyword) params.set('search', _searchKeyword);
      if (name) params.set('accountName', name);
      const exportUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      window.open(exportUrl, '_blank');
      return; 
    }
    setAccountNameHeader(name);
    // Reset pagination to first page when selecting a bank/account
    setPage(0);
    fetchTransactions(
      1,
      rowsPerPage,
      _searchKeyword,
      selectedAccount,
      name,
      selectedStatuses,
      selectedTypes,
      selectedTags,
      dateFilter,
      amountFilter,
      selectedCustomerFilter,
      selectedBankFilter,
      orderBy,
      orderDirection
    );
  }

  // Open Dialog Handler
  const handleOpenCustomerDialog = async (identifier) => {
    const customer = await buxferTransactions.getCardAndCustomerByIdentifier(identifier);
    if(customer.result.customer){
      setSelectedCustomer(customer.result.customer);
      setIsCustomerDialogOpen(true);
    }
  };

  // Close Dialog Handler
  const handleCloseCustomerDialog = () => {
    setIsCustomerDialogOpen(false);
    setSelectedCustomer(null);
  };

  return (
    <div style={{ marginTop: "20px", margin: "auto" }}>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Box
          sx={{
            width: 350,
            height: '100vh',
            position: 'sticky',
            top: 0,
            overflowY: 'auto',
            borderRight: '1px solid #ddd',  // optional separation
            bgcolor: 'background.paper',
            flexShrink: 0,
            zIndex: 1200, // make sure it stays on top if needed
          }}
        >
          <Sidebar
            accounts={accounts.filter(acc => {
                if (selectedCustomerFilter && acc.customer_name !== selectedCustomerFilter) return false;
                if (selectedBankFilter && acc.bank !== selectedBankFilter) return false;
                return true;
            })}
            selectedAccountId={selectedAccount}
            onAccountSelect={handleAccountChange}
            onBankSelect={onBankSelect}
          />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: '100vh',
            overflowY: 'auto',  // this scrolls separately
            p: 2,
          }}
        >   
        <AppBar position="static">
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between', width: '100%' }}>
              {/* Left Side: Account Name or Dashboard */}
             <div style={{ display: 'flex', alignItems: 'start' }}> 
              <Typography
                variant="h6"
                noWrap
                component="a"
                href="#app-bar-with-responsive-menu"
                sx={{
                  mr: 2,
                  display: { xs: 'none', md: 'flex' },
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: '.3rem',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
                onClick={()=>{
                  if(accountNameHeader !== ''){
                    handleOpenCustomerDialog(accountNameHeader);
                  }
                }}
              >
                {accountNameHeader === '' ? 'Dashboard' : accountNameHeader}
              </Typography>
              { accountNameHeader !== '' && 
                <IconButton
                    size="small"
                    color="error" 
                    onClick={() => {
                      console.log(searchKeyword) 
                      onBankSelect('', searchKeyword);
                    }}
                    sx={{ mr: 1 }} 
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              </div>

              {/* Right Side: Income/Expense/Total */}
              {((accountNameHeader !== '' && (income !== 0 || expense !== 0))) && (
                <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                  {income !== 0 && (
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                      Income: ${income.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  )}
                  {expense !== 0 && (
                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                      Expense: ${Math.abs(expense).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  )}
                  {income + expense !== 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: income + expense >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 700,
                      }}
                    >
                      Total: ${(income + expense).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  )}
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>

          <Container maxWidth={false} sx={{ flexGrow: 1, padding: '16px', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h1>BUXFER TRANSACTION MANAGER</h1>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="contained" color="success" onClick={() => window.location.href = '/buxfer-sync'}>
                  Sync
                </Button>
                <Button
                  variant="outlined"
                  onClick={async (e) => {
                    // try {
                    //   const resp = await buxferTransactions.getLoginsForCustomerAndBank(selectedCustomerFilter, selectedBankFilter);
                    //   if (resp.success) {
                    //     const list = resp.result || [];
                    //     alert('Logins included:\n' + list.join('\n'));
                    //   } else {
                    //     alert('No logins found');
                    //   }
                    // } catch (err) {
                    //   console.error(err);
                    //   alert('Failed to fetch logins');
                    // }
                      e.stopPropagation(); // Stop click from triggering filter
                      const url = `/buxfer-accounts`;
                      window.open(url, '_blank');
                  }}
                >
                  Show Accounts
                </Button>
              </div>
            </Box>
            <div style={{ 
              marginBottom: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              width: '100%'
             }}>

            <BuxferTransactionSmartSearch
              inputValue={searchKeyword}
              onSearchInputChange={setSearchInput}
              onSelect={(selected) => {
                // Apply search and reset to first page so it filters within selected account (AND)
                if (typeof selected === 'string') {
                  setSearchKeyword(selected);
                } else if (selected?.description) {
                  setSearchKeyword(selected.description);
                }
                setPage(0);
              }}
            />
            <Autocomplete
              disableClearable
              options={distinctCustomers}
              value={selectedCustomerFilter}
              onChange={(e, val) => {
                setSelectedCustomerFilter(val || '');
                setSelectedAccount('');
                setAccountNameHeader('');
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Customer Name" variant="outlined" />}
              style={{ width: 240 }}
            />

            <Autocomplete
              disableClearable
              options={distinctBanks}
              value={selectedBankFilter}
              onChange={(e, val) => {
                setSelectedBankFilter(val || '');
                setSelectedAccount('');
                setAccountNameHeader('');
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} label="Bank Name" variant="outlined" />}
              style={{ width: 200 }}
            />

              <TextField
                label="Amount"
                variant="outlined"
                type="number"
                value={amountFilter}
                onChange={handleAmountChange}
                style={{ width: "100px" }}
                inputProps={{ min: 0 }}
              />

              <FormControl variant="outlined" style={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusChange}
                  label="Status"
                  renderValue={(selected) => `Selected: ${selected.length}`}
                >
                  {statuses.map((stat) => (
                    <MenuItem key={stat} value={stat}>
                      <Checkbox checked={selectedStatuses.indexOf(stat) > -1} />
                      <ListItemText primary={stat} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" style={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  multiple
                  value={selectedTypes}
                  onChange={handleTypesChange}
                  label="Type"
                  renderValue={(selected) => `Selected: ${selected.length}`}
                >
                  {types.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={selectedTypes.indexOf(type) > -1} />
                      <ListItemText primary={type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" style={{ minWidth: 150 }}>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={selectedTags}
                  onChange={handleTagsChange}
                  label="Tags"
                  renderValue={(selected) => `Selected: ${selected.length}`}
                >
                  {tags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      <Checkbox checked={selectedTags.indexOf(tag) > -1} />
                      <ListItemText primary={tag} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" style={{ minWidth: 150 }}>
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  label="Date Filter"
                  disabled={startDate !== '' || endDate !== ''}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {dateFilterOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" style={{ minWidth: 150 }}>
               <TextField
                  label="Start Date"
                  type="date"
                  variant="outlined"
                  value={startDate}
                  onChange={(e) => {
                      setStartDate(e.target.value);
                      setDateFilter(''); // Optional: I-reset ang dropdown pag nag set ng date
                      setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: 160 }}
                />
              </FormControl>
              <FormControl variant="outlined" style={{ minWidth: 150 }}>
               <TextField
                  label="End Date"
                  type="date"
                  variant="outlined"
                  value={endDate}
                  onChange={(e) => {
                      setEndDate(e.target.value);
                      setDateFilter(''); // Optional: I-reset ang dropdown pag nag set ng date
                      setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: 160 }}
                />
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  console.log(searchInput)
                  setSearchKeyword(searchInput);
                  setStartDate(startDate);
                  setEndDate(endDate);
                  // Reset pagination when initiating a new search
                  setPage(0);
                }}
                style={{ height: '56px' }}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onBankSelect('');
                  setSearchKeyword('');
                  setSearchInput('');
                  setSelectedAccount('');
                  setSelectedStatuses([]);
                  setSelectedTypes([]);
                  setSelectedTags([]);
                  setDateFilter('');
                  setAmountFilter('');
                }}
                style={{ height: '56px' }}
              >
                Clear
              </Button>
            </div>
            {/* <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
              <Paper elevation={3} style={{ padding: "20px", width: "200px", textAlign: "center" }}>
                <h2 style={{ margin: 0 }}>{totalCount}</h2>
                <p style={{ margin: 0, color: "#555" }}>Transaction Count</p>
              </Paper>
            </div> */}
            <Dialog open={isCardDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                Card Details
                <IconButton
                  aria-label="close"
                  onClick={handleCloseDialog}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {selectedCard ? (
                  <>
                    <Typography gutterBottom><strong>Buxfer Card ID:</strong> {selectedCard.buxfer_card_id}</Typography>
                    <Typography gutterBottom><strong>Card Identifier:</strong> {selectedCard.card_identifier}</Typography>
                    <Typography gutterBottom><strong>Online Account ID:</strong> {selectedCard.online_account_id}</Typography>
                    <Typography gutterBottom><strong>Login ID:</strong> {selectedCard.login_id}</Typography>
                    <Typography gutterBottom><strong>Bank:</strong> {selectedCard.bank}</Typography>
                    <Typography gutterBottom><strong>Card Ending:</strong> {selectedCard.card_ending}</Typography>
                    <Typography gutterBottom><strong>Credit Line:</strong> {selectedCard.credit_line}</Typography>
                    <Typography gutterBottom><strong>Email:</strong> {selectedCard.email}</Typography>
                    <Typography gutterBottom><strong>APR:</strong> {selectedCard.apr}</Typography>
                    <Typography gutterBottom><strong>Product Code:</strong> {selectedCard.product_code}</Typography>
                    <Typography gutterBottom><strong>Product Name:</strong> {selectedCard.product_name}</Typography>
                    <Typography gutterBottom><strong>Last Synced:</strong> {selectedCard.last_synced}</Typography>
                    <Typography gutterBottom><strong>Account Type:</strong> {selectedCard.account_type}</Typography>
                  </>
                ) : (
                  <Typography>No card data available.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={isCustomerDialogOpen} onClose={handleCloseCustomerDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                Customer Details
                <IconButton
                  aria-label="close"
                  onClick={handleCloseCustomerDialog}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {selectedCustomer ? (
                  <>
                    <Typography gutterBottom><strong>Full Name:</strong> {selectedCustomer.full_name}</Typography>
                    <Typography gutterBottom><strong>CC Email:</strong> {selectedCustomer.cc_email}</Typography>
                    <Typography gutterBottom><strong>Personal Phone:</strong> {selectedCustomer.personal_phone}</Typography>
                    <Typography gutterBottom><strong>Home Address:</strong> {selectedCustomer.home_address}</Typography>
                    <Typography gutterBottom><strong>Newly Assigned Phone:</strong> {selectedCustomer.newly_assigned_phone}</Typography>
                    <Typography gutterBottom><strong>Status:</strong> {selectedCustomer.status}</Typography>
                    <Typography gutterBottom><strong>Programs:</strong> {selectedCustomer.programs}</Typography>
                    <Typography gutterBottom><strong>SMS Forwarding:</strong> {selectedCustomer.sms_forwarding}</Typography>
                    <Typography gutterBottom><strong>Can Ship To Home:</strong> {selectedCustomer.can_ship_to_home}</Typography>
                    <Typography gutterBottom><strong>PC Assigned:</strong> {selectedCustomer.pc_assigned}</Typography>
                    <Typography gutterBottom><strong>Notes:</strong> {selectedCustomer.notes}</Typography>
                  </>
                ) : (
                  <Typography>No customer data available.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseCustomerDialog}>Close</Button>
              </DialogActions>
            </Dialog>

            <DraggableTable 
                data={transactions}                // Generic prop: 'data' instead of 'transactions'
                columns={getDisplayedColumns()}    // Use helper to apply saved order
                totalCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                orderBy={orderBy}
                orderDirection={orderDirection}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onSortChange={handleOrderByChange} // Generic prop: 'onSortChange'
                onColumnsReorder={handleColumnsReorder}
              />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                ref={buttonRef}
                variant="contained"
                color="primary"
                onClick={handleExportClick}
              >
                Export
              </Button>
              <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
                <MenuItem onClick={() => handleExportFormat('csv')}>Export as CSV</MenuItem>
                <MenuItem onClick={() => handleExportFormat('excel')}>Export as Excel</MenuItem>
                <MenuItem onClick={() => handleExportFormat('PDF')}>Export as PDF</MenuItem>
              </Menu>
            </Box>
          </Container>
        </Box>
      </Box>
    </div>
  );
}

